/**
 * Soft in-app notification sound via Web Audio API.
 * Wood tick — a low, dry wooden click (~70 ms). No external asset required.
 * Unlocks playback on first user gesture to satisfy browser autoplay policies.
 */

import { randomUnitFloat } from '@utils/secureRandom'

let audioContext: AudioContext | null = null
let unlockRegistered = false

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const Ctx =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctx) return null
  if (!audioContext) {
    audioContext = new Ctx()
  }
  return audioContext
}

function createNoiseBuffer(ctx: AudioContext, durationSec: number): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * durationSec))
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < length; i += 1) {
    data[i] = randomUnitFloat() * 2 - 1
  }
  return buffer
}

function playWoodTick(ctx: AudioContext, now: number): void {
  const duration = 0.07
  const master = ctx.createGain()
  master.connect(ctx.destination)
  master.gain.setValueAtTime(0.0001, now)
  master.gain.exponentialRampToValueAtTime(1, now + 0.003)
  master.gain.exponentialRampToValueAtTime(0.0001, now + duration)

  const body = ctx.createOscillator()
  body.type = 'triangle'
  body.frequency.setValueAtTime(780, now)
  const bodyGain = ctx.createGain()
  bodyGain.gain.setValueAtTime(0.55, now)
  bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.035)
  body.connect(bodyGain)
  bodyGain.connect(master)
  body.start(now)
  body.stop(now + 0.055)

  const noiseSource = ctx.createBufferSource()
  noiseSource.buffer = createNoiseBuffer(ctx, 0.022)
  const filter = ctx.createBiquadFilter()
  filter.type = 'bandpass'
  filter.frequency.value = 1900
  filter.Q.value = 2.5
  const noiseGain = ctx.createGain()
  noiseGain.gain.setValueAtTime(0.65, now)
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.022)
  noiseSource.connect(filter)
  filter.connect(noiseGain)
  noiseGain.connect(master)
  noiseSource.start(now)
  noiseSource.stop(now + 0.032)
}

/** Resume AudioContext after a user gesture (required by most browsers). */
export function unlockNotificationSound(): void {
  const ctx = getAudioContext()
  if (!ctx || ctx.state !== 'suspended') return
  void ctx.resume().catch(() => undefined)
}

/** Call once at app startup to unlock sound on first click / key / touch. */
export function registerNotificationSoundUnlock(): void {
  if (unlockRegistered || typeof document === 'undefined') return
  unlockRegistered = true
  const unlock = () => {
    unlockNotificationSound()
    document.removeEventListener('pointerdown', unlock)
    document.removeEventListener('keydown', unlock)
    document.removeEventListener('touchstart', unlock)
  }
  document.addEventListener('pointerdown', unlock, { passive: true })
  document.addEventListener('keydown', unlock, { passive: true })
  document.addEventListener('touchstart', unlock, { passive: true })
}

/** Play the default wood-tick notification sound. */
export function playSoftNotificationSound(onComplete?: () => void): void {
  try {
    const ctx = getAudioContext()
    if (!ctx) {
      onComplete?.()
      return
    }

    if (ctx.state === 'suspended') {
      void ctx
        .resume()
        .then(() => playSoftNotificationSound(onComplete))
        .catch(() => onComplete?.())
      return
    }

    playWoodTick(ctx, ctx.currentTime)
    onComplete?.()
  } catch {
    onComplete?.()
  }
}
